import { useMemo } from "react";
import { useMatch } from "react-router-dom-v5-compat";

import { AppVariants } from "common/types/appVariant";

import { isSignedIn } from "seneca-common/features/user/state";
import { useSelector } from "seneca-common/utils/hooks/selectors";

import { useIsOnCourseHomePage } from "features/classroom/routes/classroom/routes/course/hooks/useIsOnCourseHomepages";
import {
  useIsInVocabularyAssignment,
  useIsOnCourseAssignments
} from "features/classroom/routes/classroom/routes/course/routes/assignments/hooks";
import { useIsOnCourseExamQuestions } from "features/classroom/routes/classroom/routes/course/routes/course-exam-questions-homepage/hooks";
import { useIsInSection } from "features/classroom/routes/classroom/routes/course/routes/section/hooks/useIsInSection";
import { LearningMode } from "features/classroom/state";
import useLearningMode from "features/classroom/utils/useLearningMode";
import useAppVariant from "features/common/hooks/useAppVariant";
import { ROUTES } from "routing/routes";

import { SidebarMenuItem } from "../types";
import useShowAskAmelia from "./useShowAskAmelia";
import { useShowExamPrep } from "./useShowExamPrep";

export function useSidebarMenuItemsVisible() {
  const appVariant = useAppVariant();
  const signedIn = useSelector(isSignedIn);
  const showAskAmelia = useShowAskAmelia();
  const showExamPrep = useShowExamPrep();

  return useMemo(
    () => ({
      overview: true,
      examPrep: showExamPrep && appVariant !== AppVariants.senecaPrimary,
      assignments: signedIn && appVariant !== AppVariants.senecaPrimary,
      askAmelia: showAskAmelia
    }),
    [showExamPrep, signedIn, showAskAmelia, appVariant]
  );
}

export function useSidebarMenuItemsActive(): {
  [key in SidebarMenuItem]: boolean;
} {
  const isInSection = useIsInSection();
  const isOnCourseExamQuestions = useIsOnCourseExamQuestions();
  const isOnCourseHomepage = useIsOnCourseHomePage();
  const isOnCourseAssignments = useIsOnCourseAssignments();
  const isInActiveAmeliaSession = !!useMatch(
    `${ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.relativePath}/*`
  );
  const isInVocabularyAssignment = useIsInVocabularyAssignment();
  const learningMode = useLearningMode();

  const isInAssignment = !!(
    (learningMode === LearningMode.assignments &&
      (isInSection || isOnCourseExamQuestions)) ||
    isOnCourseAssignments ||
    isInVocabularyAssignment
  );

  const isInExamPrep = !!(!isInAssignment && isOnCourseExamQuestions);

  const isInAskAmelia = !!isInActiveAmeliaSession;

  const isInOverview = isOnCourseHomepage;

  return {
    overview: isInOverview,
    examPrep: isInExamPrep,
    assignments: isInAssignment,
    askAmelia: isInAskAmelia
  };
}
